import { FullRouteDirectionVariant, FullRouteInfo } from '../../../types/gtfsTypes';
import { PolylineProps, PushpinProps } from '../../../types/MapModels';
import { RidershipStopAggRecord, RidershipStopAreaLocation } from '../../../types/ridershipTypes';

export const ENTIRE_ROUTE_DIRECTION_ID = 'all';

// Pin sizing: adapt to wide value ranges (e.g., 500–5000) using sqrt scaling
const PIN_MAIN_MIN = 1;   // px
const PIN_MAIN_MAX = 18;  // px
const PIN_OVERLAY_MIN = 1; // px
const PIN_OVERLAY_MAX = 14; // px
const ALIGHTING_MAX_CAP = 500;

function normalizeLookupKey(value: unknown): string | null {
    const normalized = String(value ?? '').trim();
    if (!normalized) return null;
    return normalized.toLowerCase();
}

function buildRowsLookup(rows: RidershipStopAggRecord[]): Map<string, RidershipStopAggRecord> {
    const lookup = new Map<string, RidershipStopAggRecord>();
    const add = (key: unknown, row: RidershipStopAggRecord) => {
        const normalized = normalizeLookupKey(key);
        if (!normalized || lookup.has(normalized)) return;
        lookup.set(normalized, row);
    };

    rows.forEach(row => {
        add(row.stopId, row);
        add(row.stopCode, row);
    });

    return lookup;
}

function resolveRowByStop(lookup: Map<string, RidershipStopAggRecord>, stop: { stopId?: string; stopCode?: string; }): RidershipStopAggRecord | undefined {
    const byStopId = normalizeLookupKey(stop.stopId);
    if (byStopId && lookup.has(byStopId)) {
        return lookup.get(byStopId);
    }
    const byStopCode = normalizeLookupKey(stop.stopCode);
    if (byStopCode && lookup.has(byStopCode)) {
        return lookup.get(byStopCode);
    }
    return undefined;
}

function buildRowLookupKey(stopId?: string, stopCode?: string, stopSequence?: number): string | null {
    const stopKey = normalizeLookupKey(stopId) || normalizeLookupKey(stopCode);
    if (!stopKey) {
        return null;
    }
    if (typeof stopSequence === 'number' && Number.isFinite(stopSequence)) {
        return `${stopKey}|${stopSequence}`;
    }
    return stopKey;
}

function buildRowsLookupWithSequence(rows: RidershipStopAggRecord[]): Map<string, RidershipStopAggRecord> {
    const lookup = new Map<string, RidershipStopAggRecord>();
    const add = (key: string | null, row: RidershipStopAggRecord) => {
        if (!key || lookup.has(key)) {
            return;
        }
        lookup.set(key, row);
    };

    rows.forEach(row => {
        add(buildRowLookupKey(row.stopId, row.stopCode, row.stopSequence), row);
        add(buildRowLookupKey(row.stopId, row.stopCode), row);
        add(normalizeLookupKey(row.stopId), row);
        add(normalizeLookupKey(row.stopCode), row);
    });

    return lookup;
}

function resolveRowByStopAndSequence(
    lookup: Map<string, RidershipStopAggRecord>,
    stop: { stopId?: string; stopCode?: string; },
    stopSequence?: number,
): RidershipStopAggRecord | undefined {
    const sequenceKey = buildRowLookupKey(stop.stopId, stop.stopCode, stopSequence);
    if (sequenceKey && lookup.has(sequenceKey)) {
        return lookup.get(sequenceKey);
    }
    return resolveRowByStop(lookup, stop);
}

interface RidershipPinMaxValues {
    maxBoard: number;
    maxAlight: number;
    maxLoad: number;
}

interface RidershipMapVizMutable {
    loadPins: PushpinProps[];
    alightPins: PushpinProps[];
    boardPins: PushpinProps[];
    labels: string[];
    stopIds: string[];
    board: number[];
    alight: number[];
    load: number[];
}

function computeVariantMaxValues(
    variant: FullRouteDirectionVariant,
    rowsLookup: Map<string, RidershipStopAggRecord>,
    useSequenceMatch: boolean,
): RidershipPinMaxValues {
    let maxBoard = 1;
    let maxAlight = 1;
    let maxLoad = 1;

    for (const stopInfo of variant.stops || []) {
        const rid = useSequenceMatch
            ? resolveRowByStopAndSequence(rowsLookup, stopInfo.stop, stopInfo.ordinal)
            : resolveRowByStop(rowsLookup, stopInfo.stop);
        const b = Math.max(0, Number(rid?.boarding) || 0);
        const a = Math.max(0, Number(rid?.alighting) || 0);
        const l = Math.max(0, Number(rid?.load) || 0);
        if (b > maxBoard) maxBoard = b;
        if (a > maxAlight) maxAlight = a;
        if (l > maxLoad) maxLoad = l;
    }

    return { maxBoard, maxAlight, maxLoad };
}

function mergeMaxValues(left: RidershipPinMaxValues, right: RidershipPinMaxValues): RidershipPinMaxValues {
    return {
        maxBoard: Math.max(left.maxBoard, right.maxBoard),
        maxAlight: Math.max(left.maxAlight, right.maxAlight),
        maxLoad: Math.max(left.maxLoad, right.maxLoad),
    };
}

function appendVariantStopsToViz(
    variant: FullRouteDirectionVariant,
    rowsLookup: Map<string, RidershipStopAggRecord>,
    viz: RidershipMapVizMutable,
    maxValues: RidershipPinMaxValues,
    useSequenceMatch: boolean,
): void {
    for (const stopInfo of variant.stops || []) {
        const rid = useSequenceMatch
            ? resolveRowByStopAndSequence(rowsLookup, stopInfo.stop, stopInfo.ordinal)
            : resolveRowByStop(rowsLookup, stopInfo.stop);
        const stopIdKey = String(rid?.stopId ?? stopInfo.stop.stopId ?? stopInfo.stop.stopCode ?? viz.labels.length);
        const stopName = String(stopInfo.stop.stopName);
        const label = `${stopName} (${stopIdKey})`;
        viz.labels.push(label);
        viz.stopIds.push(stopIdKey);
        const b = Math.max(0, Number(rid?.boarding) || 0);
        const a = Math.max(0, Number(rid?.alighting) || 0);
        const l = Math.max(0, Number(rid?.load) || 0);
        viz.board.push(b);
        viz.alight.push(a);
        viz.load.push(l);

        const loadRadius = scaleRadius(l, maxValues.maxLoad, PIN_MAIN_MIN, PIN_MAIN_MAX);
        const alightRadius = scaleRadiusAlight(a, maxValues.maxAlight, PIN_OVERLAY_MIN, PIN_OVERLAY_MAX);
        const boardRadius = scaleRadius(b, maxValues.maxBoard, PIN_OVERLAY_MIN, PIN_OVERLAY_MAX);
        const lat = stopInfo.stop.lat as Latitude;
        const lng = stopInfo.stop.lon as Longitude;
        const baseOptions = { stopId: stopIdKey, stopName, boarding: b, alighting: a, load: l };

        viz.loadPins.push({ location: [lat, lng], options: { title: label + ' (load)', color: '#2980b9', icon: 'stopIcon', radius: loadRadius, ...baseOptions }, eventHandlers: [] });
        viz.alightPins.push({ location: [lat, lng], options: { title: label + ' (alight)', color: '#f39c12', icon: 'stopIcon', radius: alightRadius, ...baseOptions }, eventHandlers: [] });
        viz.boardPins.push({ location: [lat, lng], options: { title: label + ' (board)', color: '#2ecc71', icon: 'stopIcon', radius: boardRadius, ...baseOptions }, eventHandlers: [] });
    }
}

function appendVariantPolylines(variant: FullRouteDirectionVariant, polylines: PolylineProps[]): void {
    polylines.push({
        options: { strokeColor: '#026db5', strokeThickness: 3 },
        locations: variant.shape.points.map(p => [p.lat as Latitude, p.lon as Longitude]),
    });
}

function isEntireRouteDirectionVariantId(directionVariantId: string): boolean {
    return directionVariantId.trim().toLowerCase() === ENTIRE_ROUTE_DIRECTION_ID;
}

function buildRowsOnlyViz(
    routeInfo: FullRouteInfo | null,
    rows: RidershipStopAggRecord[],
    polylines: PolylineProps[],
): RidershipMapViz {
    const loadPins: PushpinProps[] = [];
    const alightPins: PushpinProps[] = [];
    const boardPins: PushpinProps[] = [];
    const labels: string[] = [];
    const stopIds: string[] = [];
    const board: number[] = [];
    const alight: number[] = [];
    const load: number[] = [];

    const stopsLookup = new Map<string, { stopName: string; lat: Latitude; lon: Longitude; stopId?: string; stopCode?: string }>();
    (routeInfo?.directions || []).forEach(direction => {
        (direction.directionVariants || []).forEach(variant => {
            (variant.stops || []).forEach(stopInfo => {
                const stop = stopInfo.stop;
                const register = (rawKey: unknown) => {
                    const key = normalizeLookupKey(rawKey);
                    if (!key || stopsLookup.has(key)) return;
                    stopsLookup.set(key, {
                        stopName: stop.stopName,
                        lat: stop.lat as Latitude,
                        lon: stop.lon as Longitude,
                        stopId: stop.stopId,
                        stopCode: stop.stopCode,
                    });
                };
                register(stop.stopId);
                register(stop.stopCode);
            });
        });
    });

    const sortedRows = [...rows]
        .filter(row => row.stopId !== null && row.stopId !== undefined)
        .sort((left, right) => {
            const leftSeq = typeof left.stopSequence === 'number' && Number.isFinite(left.stopSequence) ? left.stopSequence : Number.POSITIVE_INFINITY;
            const rightSeq = typeof right.stopSequence === 'number' && Number.isFinite(right.stopSequence) ? right.stopSequence : Number.POSITIVE_INFINITY;
            return leftSeq - rightSeq;
        });

    let maxBoard = 1;
    let maxAlight = 1;
    let maxLoad = 1;
    sortedRows.forEach(row => {
        const b = Math.max(0, Number(row.boarding) || 0);
        const a = Math.max(0, Number(row.alighting) || 0);
        const l = Math.max(0, Number(row.load) || 0);
        if (b > maxBoard) maxBoard = b;
        if (a > maxAlight) maxAlight = a;
        if (l > maxLoad) maxLoad = l;
    });

    sortedRows.forEach((row, index) => {
        const rowStopId = String(row.stopId ?? row.stopCode ?? index);
        const rowStopName = String(row.stopName || 'Stop');
        const label = `${rowStopName} (${rowStopId})`;
        const b = Math.max(0, Number(row.boarding) || 0);
        const a = Math.max(0, Number(row.alighting) || 0);
        const l = Math.max(0, Number(row.load) || 0);

        labels.push(label);
        stopIds.push(rowStopId);
        board.push(b);
        alight.push(a);
        load.push(l);

        const stopKey = normalizeLookupKey(row.stopId) || normalizeLookupKey(row.stopCode);
        const stop = stopKey ? stopsLookup.get(stopKey) : undefined;
        if (!stop) return;

        const loadRadius = scaleRadius(l, maxLoad, PIN_MAIN_MIN, PIN_MAIN_MAX);
        const alightRadius = scaleRadiusAlight(a, maxAlight, PIN_OVERLAY_MIN, PIN_OVERLAY_MAX);
        const boardRadius = scaleRadius(b, maxBoard, PIN_OVERLAY_MIN, PIN_OVERLAY_MAX);
        const baseOptions = { stopId: rowStopId, stopName: rowStopName, boarding: b, alighting: a, load: l };

        loadPins.push({ location: [stop.lat, stop.lon], options: { title: `${label} (load)`, color: '#2980b9', icon: 'stopIcon', radius: loadRadius, ...baseOptions }, eventHandlers: [] });
        alightPins.push({ location: [stop.lat, stop.lon], options: { title: `${label} (alight)`, color: '#f39c12', icon: 'stopIcon', radius: alightRadius, ...baseOptions }, eventHandlers: [] });
        boardPins.push({ location: [stop.lat, stop.lon], options: { title: `${label} (board)`, color: '#2ecc71', icon: 'stopIcon', radius: boardRadius, ...baseOptions }, eventHandlers: [] });
    });

    return { loadPins, alightPins, boardPins, labels, board, alight, load, polylines, stopIds };
}

function scaleRadius(value: number, maxValue: number, minR: number, maxR: number): number {
    const v = Math.max(0, Number(value) || 0);
    const m = Math.max(1, Number(maxValue) || 1);
    const t = Math.sqrt(v / m); // compress large ranges while keeping relative differences
    const r = minR + (maxR - minR) * t;
    return Math.round(Math.max(minR, Math.min(maxR, r)));
}

// Alighting values are typically within 0–500; cap max for better visual differentiation of small values
function scaleRadiusAlight(value: number, maxValue: number, minR: number, maxR: number): number {
    const effectiveMax = Math.max(1, Math.min(Number(maxValue) || 1, ALIGHTING_MAX_CAP));
    return scaleRadius(value, effectiveMax, minR, maxR);
}

export interface RidershipMapViz {
    loadPins: PushpinProps[];
    alightPins: PushpinProps[];
    boardPins: PushpinProps[];
    labels: string[];
    board: number[];
    alight: number[];
    load: number[];
    polylines: PolylineProps[];
    stopIds: string[];
}

export function buildBubblePinsFromStopActivity(
    stops: RidershipStopAreaLocation[] | null | undefined,
    rows: RidershipStopAggRecord[] | null | undefined,
): Pick<RidershipMapViz, 'loadPins' | 'alightPins' | 'boardPins'> {
    const loadPins: PushpinProps[] = [];
    const alightPins: PushpinProps[] = [];
    const boardPins: PushpinProps[] = [];

    const stopLookup = new Map<string, RidershipStopAreaLocation>();
    (stops || []).forEach(stop => {
        if (stop.stopId) {
            stopLookup.set(String(stop.stopId), stop);
        }
        if (stop.stopCode) {
            stopLookup.set(String(stop.stopCode), stop);
        }
    });

    const rowsWithCoords: Array<{ row: RidershipStopAggRecord; stop: RidershipStopAreaLocation }> = [];
    (rows || []).forEach(row => {
        if (row?.stopId === null || row?.stopId === undefined) return;
        const stop = stopLookup.get(String(row.stopId));
        if (!stop) return;
        rowsWithCoords.push({ row, stop });
    });

    if (rowsWithCoords.length === 0) {
        return { loadPins, alightPins, boardPins };
    }

    let maxBoard = 1;
    let maxAlight = 1;
    let maxLoad = 1;
    rowsWithCoords.forEach(({ row }) => {
        const b = Math.max(0, Number(row.boarding) || 0);
        const a = Math.max(0, Number(row.alighting) || 0);
        const l = Math.max(0, Number(row.load) || 0);
        if (b > maxBoard) maxBoard = b;
        if (a > maxAlight) maxAlight = a;
        if (l > maxLoad) maxLoad = l;
    });

    rowsWithCoords.forEach(({ row, stop }) => {
        const stopIdKey = String(row.stopId ?? stop.stopId ?? stop.stopCode ?? '');
        const stopName = String(row.stopName || stop.stopName || 'Stop');
        const label = `${stopName} (${stopIdKey})`;
        const b = Math.max(0, Number(row.boarding) || 0);
        const a = Math.max(0, Number(row.alighting) || 0);
        const l = Math.max(0, Number(row.load) || 0);
        const loadRadius = scaleRadius(l, maxLoad, PIN_MAIN_MIN, PIN_MAIN_MAX);
        const alightRadius = scaleRadiusAlight(a, maxAlight, PIN_OVERLAY_MIN, PIN_OVERLAY_MAX);
        const boardRadius = scaleRadius(b, maxBoard, PIN_OVERLAY_MIN, PIN_OVERLAY_MAX);
        const lat = stop.latitude as Latitude;
        const lng = stop.longitude as Longitude;
        const baseOptions = { stopId: stopIdKey, stopName, boarding: b, alighting: a, load: l };

        loadPins.push({ location: [lat, lng], options: { title: label + ' (load)', color: '#2980b9', icon: 'stopIcon', radius: loadRadius, ...baseOptions }, eventHandlers: [] });
        alightPins.push({ location: [lat, lng], options: { title: label + ' (alight)', color: '#f39c12', icon: 'stopIcon', radius: alightRadius, ...baseOptions }, eventHandlers: [] });
        boardPins.push({ location: [lat, lng], options: { title: label + ' (board)', color: '#2ecc71', icon: 'stopIcon', radius: boardRadius, ...baseOptions }, eventHandlers: [] });
    });

    return { loadPins, alightPins, boardPins };
}

// Build visualization directly from GTFS (order + coordinates) and API rows (values), no aggregation
export function buildVizFromGtfsAndRows(
    routeInfo: FullRouteInfo | null,
    directionVariantId: string,
    directionVariantInternalIds: string | string[] | undefined,
    ridRows: RidershipStopAggRecord[] | null,
): RidershipMapViz {
    const loadPins: PushpinProps[] = [];
    const alightPins: PushpinProps[] = [];
    const boardPins: PushpinProps[] = [];
    const labels: string[] = [];
    const stopIds: string[] = [];
    const board: number[] = [];
    const alight: number[] = [];
    const load: number[] = [];
    const polylines: PolylineProps[] = [];

    const rowsAll = ridRows || [];
    const hasInternal = rowsAll.some(r => !!r.directionVariantId);
    const normalizedInternalIds = (Array.isArray(directionVariantInternalIds)
        ? directionVariantInternalIds
        : [directionVariantInternalIds])
        .map(id => String(id ?? '').trim())
        .filter(id => id.length > 0);
    const normalizedInternalIdsSet = new Set(normalizedInternalIds.map(id => id.toLowerCase()));
    const effectiveRows = (hasInternal && normalizedInternalIdsSet.size > 0)
        ? rowsAll.filter(r => normalizedInternalIdsSet.has(String(r.directionVariantId ?? '').trim().toLowerCase()))
        : rowsAll;

    if (!routeInfo || !directionVariantId) {
        return buildRowsOnlyViz(routeInfo, effectiveRows, polylines);
    }

    const allVariants = (routeInfo.directions || [])
        .flatMap(direction => direction.directionVariants || []);

    if (isEntireRouteDirectionVariantId(directionVariantId)) {
        if (allVariants.length === 0) {
            return buildRowsOnlyViz(routeInfo, effectiveRows, polylines);
        }

        const rowsLookup = buildRowsLookupWithSequence(effectiveRows);
        allVariants.forEach(variant => appendVariantPolylines(variant, polylines));

        let maxValues: RidershipPinMaxValues = { maxBoard: 1, maxAlight: 1, maxLoad: 1 };
        allVariants.forEach(variant => {
            maxValues = mergeMaxValues(maxValues, computeVariantMaxValues(variant, rowsLookup, true));
        });

        const viz: RidershipMapVizMutable = { loadPins, alightPins, boardPins, labels, stopIds, board, alight, load };
        allVariants.forEach(variant => appendVariantStopsToViz(variant, rowsLookup, viz, maxValues, true));

        return { loadPins, alightPins, boardPins, labels, board, alight, load, polylines, stopIds };
    }

    // Find selected variant by directionVariantId
    const selectedVariants: FullRouteDirectionVariant[] = [];
    for (const d of routeInfo.directions || []) {
        const v = d.directionVariants.find(x => x.directionVariantId === directionVariantId);
        if (v) selectedVariants.push(v);
    }

    const rowsByStopId = buildRowsLookup(effectiveRows);
    if (!selectedVariants.length && allVariants.length > 0 && rowsByStopId.size > 0) {
        let bestVariant: FullRouteDirectionVariant | null = null;
        let bestScore = 0;
        for (const variant of allVariants) {
            let score = 0;
            for (const stopInfo of variant.stops || []) {
                if (resolveRowByStop(rowsByStopId, stopInfo.stop)) {
                    score++;
                }
            }
            if (score > bestScore) {
                bestScore = score;
                bestVariant = variant;
            }
        }
        if (bestVariant && bestScore > 0) {
            selectedVariants.push(bestVariant);
        }
    }
    if (!selectedVariants.length) {
        for (const variant of allVariants) {
            appendVariantPolylines(variant, polylines);
        }
        return buildRowsOnlyViz(routeInfo, effectiveRows, polylines);
    }

    for (const variant of selectedVariants) {
        appendVariantPolylines(variant, polylines);
    }

    let maxValues: RidershipPinMaxValues = { maxBoard: 1, maxAlight: 1, maxLoad: 1 };
    for (const variant of selectedVariants) {
        maxValues = mergeMaxValues(maxValues, computeVariantMaxValues(variant, rowsByStopId, false));
    }

    const viz: RidershipMapVizMutable = { loadPins, alightPins, boardPins, labels, stopIds, board, alight, load };
    for (const variant of selectedVariants) {
        appendVariantStopsToViz(variant, rowsByStopId, viz, maxValues, false);
    }

    return { loadPins, alightPins, boardPins, labels, board, alight, load, polylines, stopIds };
}
